import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react'

import { ApiError, api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { AssistantContextStep } from '@/components/AssistantContextStep'
import { AssistantConstraintsStep } from '@/components/AssistantConstraintsStep'
import { AssistantExamplesStep } from '@/components/AssistantExamplesStep'
import { AssistantResultStep } from '@/components/AssistantResultStep'
import { NewPromptDialog } from '@/components/NewPromptDialog'
import { Toast, ToastDescription, ToastTitle } from '@/components/ui/toast'
import type {
  AssistantApplyResponse,
  AssistantExample,
  AssistantGenerateResponse,
  AssistantTone,
} from '@sdr-agend/admin-schemas'

interface WizardState {
  campaignId: string
  objective: string
  tone: AssistantTone
  constraints: string[]
  examples: AssistantExample[]
}

const INITIAL_STATE: WizardState = {
  campaignId: '',
  objective: '',
  tone: 'amigavel',
  constraints: [],
  examples: [],
}

interface ToastState {
  open: boolean
  variant: 'default' | 'destructive'
  title: string
  description?: string
}

interface BannedPatternError {
  code: 'BANNED_PATTERN_IN_GENERATION'
  offenders: string[]
  suggestion?: string
}

const STEPS = [
  { id: 'context', label: 'Contexto' },
  { id: 'constraints', label: 'Restrições' },
  { id: 'examples', label: 'Exemplos' },
  { id: 'result', label: 'Resultado' },
] as const

type StepId = (typeof STEPS)[number]['id']

interface AssistantWizardProps {
  /** Default initial state — used by tests + when re-entering after navigation. */
  initial?: Partial<WizardState>
}

/**
 * Story 3.18 — PromptAssistant wizard (4 steps).
 *
 * Holds the form state, drives the generate mutation, and renders the active
 * step. Banned-pattern 422 surfaces a retry CTA that auto-injects the
 * offenders as additional constraints (§AC #9).
 */
export function AssistantWizard({ initial }: AssistantWizardProps): React.ReactElement {
  const [state, setState] = useState<WizardState>({ ...INITIAL_STATE, ...initial })
  const [step, setStep] = useState<StepId>('context')
  const [result, setResult] = useState<AssistantGenerateResponse | null>(null)
  const [bannedError, setBannedError] = useState<BannedPatternError | null>(null)
  const [toast, setToast] = useState<ToastState>({
    open: false,
    variant: 'default',
    title: '',
  })
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogPrompt, setDialogPrompt] = useState('')
  const stepRefs = useRef<Map<StepId, HTMLDivElement | null>>(new Map())

  const generateMutation = useMutation({
    mutationFn: async () =>
      api<AssistantGenerateResponse>('/admin/prompts/assistant/generate', {
        method: 'POST',
        body: JSON.stringify({
          ...(state.campaignId !== '' ? { campaignId: state.campaignId } : {}),
          objective: state.objective,
          tone: state.tone,
          constraints: state.constraints,
          examples: state.examples.filter((e) => e.q.trim() !== '' && e.a.trim() !== ''),
        }),
      }),
    onSuccess: (res) => {
      setResult(res)
      setBannedError(null)
      setStep('result')
    },
    onError: (err: Error) => {
      if (err instanceof ApiError && err.status === 422) {
        const body = err.bodyJson as { error?: BannedPatternError } | null
        if (body?.error?.code === 'BANNED_PATTERN_IN_GENERATION') {
          setBannedError(body.error)
          return
        }
      }
      if (err instanceof ApiError && err.status === 429) {
        setToast({
          open: true,
          variant: 'destructive',
          title: 'Limite de geração atingido',
          description: 'Aguarde um minuto antes de tentar novamente.',
        })
        return
      }
      setToast({
        open: true,
        variant: 'destructive',
        title: 'Falha ao gerar prompt',
        description: err.message,
      })
    },
  })

  // Focus the active step's container on change for keyboard navigation (§AC #10).
  useEffect(() => {
    const node = stepRefs.current.get(step)
    if (node !== null && node !== undefined) {
      node.focus()
    }
  }, [step])

  const stepIndex = STEPS.findIndex((s) => s.id === step)

  function goPrev(): void {
    const prev = STEPS[stepIndex - 1]
    if (prev !== undefined) setStep(prev.id)
  }
  function goNext(): void {
    const next = STEPS[stepIndex + 1]
    if (next !== undefined) setStep(next.id)
  }

  const canGoNext = useMemo(() => {
    if (step === 'context') return state.objective.trim().length >= 10
    return true
  }, [step, state.objective])

  function retryWithInjectedConstraints(): void {
    if (bannedError === null) return
    const merged = Array.from(
      new Set<string>([...state.constraints, ...bannedError.offenders]),
    )
    setState((s) => ({ ...s, constraints: merged }))
    setBannedError(null)
    generateMutation.mutate()
  }

  function setRef(id: StepId) {
    return (el: HTMLDivElement | null): void => {
      stepRefs.current.set(id, el)
    }
  }

  return (
    <div className="grid gap-6">
      {/* Step indicator -------------------------------------------------- */}
      <nav aria-label="Passos do wizard">
        <ol className="flex flex-wrap items-center gap-2">
          {STEPS.map((s, idx) => {
            const isActive = s.id === step
            const isPast = idx < stepIndex
            return (
              <li key={s.id} className="flex items-center gap-2">
                <button
                  type="button"
                  aria-current={isActive ? 'step' : undefined}
                  onClick={() => {
                    // Allow navigating back to past steps via clicks.
                    if (idx <= stepIndex) setStep(s.id)
                  }}
                  className={
                    'inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm ' +
                    (isActive
                      ? 'border-primary bg-primary/10 font-medium text-primary'
                      : isPast
                        ? 'border-muted bg-muted/30 text-foreground hover:bg-muted'
                        : 'border-muted-foreground/30 text-muted-foreground')
                  }
                  disabled={idx > stepIndex}
                >
                  <span
                    className={
                      'inline-flex h-5 w-5 items-center justify-center rounded-full text-xs ' +
                      (isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted-foreground/20 text-muted-foreground')
                    }
                  >
                    {idx + 1}
                  </span>
                  {s.label}
                </button>
                {idx < STEPS.length - 1 && (
                  <ChevronRight
                    className="h-4 w-4 text-muted-foreground"
                    aria-hidden="true"
                  />
                )}
              </li>
            )
          })}
        </ol>
      </nav>

      {/* Banned-pattern banner ------------------------------------------- */}
      {bannedError !== null && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
        >
          <p className="font-medium">Output bloqueado pelo validador de segurança.</p>
          <p className="text-xs">
            Padrões ofensores: <strong>{bannedError.offenders.join(', ')}</strong>
          </p>
          {bannedError.suggestion !== undefined && (
            <p className="mt-1 text-xs">{bannedError.suggestion}</p>
          )}
          <div className="mt-2">
            <Button type="button" size="sm" onClick={retryWithInjectedConstraints}>
              Tentar novamente injetando como restrições
            </Button>
          </div>
        </div>
      )}

      {/* Loading state --------------------------------------------------- */}
      {generateMutation.isPending && (
        <div
          role="status"
          aria-live="polite"
          className="rounded-md border bg-muted/20 p-4 text-sm"
        >
          <p className="font-medium">Gerando prompt — pode levar até 15s.</p>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full w-1/3 animate-pulse rounded-full bg-primary/50" />
          </div>
        </div>
      )}

      {/* Active step ------------------------------------------------------ */}
      <div
        ref={setRef(step)}
        tabIndex={-1}
        className="rounded-md border bg-background p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        data-testid={`assistant-step-${step}`}
      >
        {step === 'context' && (
          <AssistantContextStep
            campaignId={state.campaignId}
            objective={state.objective}
            tone={state.tone}
            onChange={(next) => setState((s) => ({ ...s, ...next }))}
            disabled={generateMutation.isPending}
          />
        )}
        {step === 'constraints' && (
          <AssistantConstraintsStep
            constraints={state.constraints}
            onChange={(next) => setState((s) => ({ ...s, constraints: next }))}
            disabled={generateMutation.isPending}
          />
        )}
        {step === 'examples' && (
          <AssistantExamplesStep
            examples={state.examples}
            onChange={(next) => setState((s) => ({ ...s, examples: next }))}
            disabled={generateMutation.isPending}
          />
        )}
        {step === 'result' && result !== null && (
          <AssistantResultStep
            result={result}
            campaignId={state.campaignId}
            onRefine={() => setStep('context')}
            onEditManually={(prompt) => {
              setDialogPrompt(prompt)
              setDialogOpen(true)
            }}
            onApplied={(_applied: AssistantApplyResponse) => {
              setToast({
                open: true,
                variant: 'default',
                title: 'Aplicado com sucesso',
                description: 'O prompt foi vinculado à campanha.',
              })
            }}
          />
        )}
      </div>

      {/* Navigation footer ----------------------------------------------- */}
      {step !== 'result' && (
        <div className="flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={goPrev}
            disabled={stepIndex === 0 || generateMutation.isPending}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            Voltar
          </Button>

          {step === 'examples' ? (
            <Button
              type="button"
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending || state.objective.trim().length < 10}
            >
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              {generateMutation.isPending ? 'Gerando…' : 'Gerar prompt'}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={goNext}
              disabled={!canGoNext || generateMutation.isPending}
            >
              Avançar
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          )}
        </div>
      )}

      <NewPromptDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        defaultSystemPrompt={dialogPrompt}
        defaultCampaignId={state.campaignId}
      />

      <Toast
        open={toast.open}
        onOpenChange={(o) => setToast((s) => ({ ...s, open: o }))}
        variant={toast.variant}
      >
        <div className="grid gap-1">
          <ToastTitle>{toast.title}</ToastTitle>
          {toast.description !== undefined && (
            <ToastDescription>{toast.description}</ToastDescription>
          )}
        </div>
      </Toast>
    </div>
  )
}
